import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { meetingAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Monitor,
  MonitorOff,
  Clock,
  X,
  AlertTriangle,
  Maximize2,
  Minimize2,
  ExternalLink,
  Search,
  Users,
  Wifi,
  WifiOff,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { io } from "socket.io-client";

const STATUS_MAP = {
  sharing:      { color: "bg-green-500", textColor: "text-green-600", icon: Monitor, label: "Actif", ring: "ring-green-500/30" },
  pending:      { color: "bg-yellow-500", textColor: "text-yellow-600", icon: Clock, label: "En attente", ring: "ring-yellow-500/30" },
  stopped:      { color: "bg-red-500", textColor: "text-red-600", icon: MonitorOff, label: "Coupé", ring: "ring-red-500/30" },
  refused:      { color: "bg-gray-600", textColor: "text-gray-500", icon: X, label: "Refusé", ring: "ring-gray-500/30" },
  error:        { color: "bg-orange-500", textColor: "text-orange-500", icon: AlertTriangle, label: "Erreur", ring: "ring-orange-500/30" },
  disconnected: { color: "bg-gray-400", textColor: "text-gray-400", icon: WifiOff, label: "Déconnecté", ring: "ring-gray-400/30" },
};

export default function ScreenMonitorPage() {
  const { workspaceId, meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]); // [{user_id, user_name, status}]
  const [filter, setFilter] = useState("");
  const [focusedUser, setFocusedUser] = useState(null);
  const [, forceUpdate] = useState(0); // Force re-render when streams arrive

  const socketRef = useRef(null);
  const peerConnections = useRef({}); // {user_id: RTCPeerConnection}
  const remoteStreams = useRef({}); // {user_id: MediaStream}  <-- store streams here
  const videoRefs = useRef({}); // {user_id: HTMLVideoElement}

  // Helper: attach stream to video element
  const attachStream = useCallback((userId) => {
    const el = videoRefs.current[userId];
    const stream = remoteStreams.current[userId];
    if (el && stream) {
      if (el.srcObject !== stream) {
        el.srcObject = stream;
        el.play().catch(() => {}); // Auto-play might need explicit call
      }
    }
  }, []);

  // Load meeting info
  useEffect(() => {
    const load = async () => {
      try {
        const res = await meetingAPI.get(workspaceId, meetingId);
        setMeeting(res.data);
      } catch (e) {
        toast.error("Réunion introuvable");
        navigate(`/workspace/${workspaceId}/meetings`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, meetingId, navigate]);

  // Connect Socket.IO as monitor
  useEffect(() => {
    if (!user) return;

    const socket = io(window.location.origin, {
      path: "/ws/socket.io",
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      socket.emit("screen:join", {
        meeting_id: meetingId,
        user_id: user.id,
        user_name: `${user.first_name} ${user.last_name}`,
        role: "monitor",
      });
    });

    // Receive initial sessions list
    socket.on("screen:sessions", (data) => {
      setSessions(data || []);
    });

    // Receive status updates
    socket.on("screen:status", (data) => {
      setSessions((prev) => {
        const existing = prev.find((s) => s.user_id === data.user_id);
        if (existing) {
          return prev.map((s) =>
            s.user_id === data.user_id ? { ...s, status: data.status, user_name: data.user_name || s.user_name } : s
          );
        }
        return [...prev, { user_id: data.user_id, user_name: data.user_name, status: data.status }];
      });
    });

    // Receive SDP offer from candidate
    socket.on("screen:offer", async (data) => {
      try {
        // Close existing connection for this user if any
        if (peerConnections.current[data.user_id]) {
          peerConnections.current[data.user_id].close();
        }

        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
            {
              urls: "turn:openrelay.metered.ca:80",
              username: "openrelayproject",
              credential: "openrelayproject",
            },
            {
              urls: "turn:openrelay.metered.ca:443",
              username: "openrelayproject",
              credential: "openrelayproject",
            },
            {
              urls: "turn:openrelay.metered.ca:443?transport=tcp",
              username: "openrelayproject",
              credential: "openrelayproject",
            },
          ],
        });

        // When we get a remote stream, STORE it and attach to video
        pc.ontrack = (event) => {
          const stream = event.streams?.[0] || new MediaStream([event.track]);
          remoteStreams.current[data.user_id] = stream;
          // Try to attach immediately
          attachStream(data.user_id);
          // Force React re-render so video element gets created/updated
          forceUpdate((n) => n + 1);
          // Also try again after a short delay (DOM might not be ready)
          setTimeout(() => attachStream(data.user_id), 300);
          setTimeout(() => attachStream(data.user_id), 1000);
        };

        // Send ICE candidates back
        pc.onicecandidate = (e) => {
          if (e.candidate) {
            socket.emit("screen:ice", {
              user_id: data.user_id,
              candidate: e.candidate.toJSON(),
            });
          }
        };

        pc.onconnectionstatechange = () => {
          if (pc.connectionState === "connected") {
            // Connection established, try attach again
            setTimeout(() => attachStream(data.user_id), 200);
            setTimeout(() => attachStream(data.user_id), 1000);
          }
        };

        // Set remote offer and create answer
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        // Explicitly serialize SDP to plain object
        socket.emit("screen:answer", {
          user_id: data.user_id,
          sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
        });

        peerConnections.current[data.user_id] = pc;
      } catch (err) {
        console.error("Error handling offer from", data.user_id, err);
      }
    });

    // Receive ICE candidate from candidate
    socket.on("screen:ice", async (data) => {
      try {
        const pc = peerConnections.current[data.user_id];
        if (pc && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      } catch (err) {
        console.error("Error adding ICE candidate:", err);
      }
    });

    socketRef.current = socket;

    return () => {
      Object.values(peerConnections.current).forEach((pc) => pc.close());
      peerConnections.current = {};
      remoteStreams.current = {};
      socket.disconnect();
    };
  }, [meetingId, user, attachStream]);

  // Register video ref for a user — also attach stream if already available
  const setVideoRef = useCallback((userId, el) => {
    if (el) {
      videoRefs.current[userId] = el;
      // Attach stored stream if we already have one
      attachStream(userId);
    }
  }, [attachStream]);

  // Re-attach streams whenever sessions change (video elements might have been re-created)
  useEffect(() => {
    sessions.forEach((s) => {
      if (s.status === "sharing") {
        // Small delay to ensure DOM is ready
        setTimeout(() => attachStream(s.user_id), 100);
      }
    });
  }, [sessions, attachStream]);

  // Open focused view in new window
  const openInNewWindow = (userId, userName) => {
    const popup = window.open("", `screen_${userId}`, "width=1280,height=720,menubar=no,toolbar=no");
    if (!popup) {
      toast.error("Popup bloqué par le navigateur");
      return;
    }
    popup.document.title = `Écran — ${userName}`;
    popup.document.body.style.cssText = "margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh;";
    const video = popup.document.createElement("video");
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    video.style.cssText = "max-width:100%;max-height:100%;";

    // Use stored stream
    const stream = remoteStreams.current[userId];
    if (stream) {
      video.srcObject = stream;
    }
    popup.document.body.appendChild(video);
  };

  // Filter sessions
  const filteredSessions = sessions.filter((s) =>
    !filter || s.user_name?.toLowerCase().includes(filter.toLowerCase())
  );

  // Sort: sharing first, then pending, then rest
  const sortOrder = { sharing: 0, pending: 1, error: 2, stopped: 3, refused: 4, disconnected: 5 };
  filteredSessions.sort((a, b) => (sortOrder[a.status] ?? 9) - (sortOrder[b.status] ?? 9));

  // Stats
  const stats = {
    total: sessions.length,
    sharing: sessions.filter((s) => s.status === "sharing").length,
    pending: sessions.filter((s) => s.status === "pending").length,
    stopped: sessions.filter((s) => ["stopped", "refused", "disconnected", "error"].includes(s.status)).length,
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  // Focused view (fullscreen modal)
  if (focusedUser) {
    const session = sessions.find((s) => s.user_id === focusedUser);
    const cfg = STATUS_MAP[session?.status] || STATUS_MAP.disconnected;
    const StatusIcon = cfg.icon;

    const toggleFullscreen = () => {
      const el = document.getElementById("monitor-fullscreen-container");
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        el.requestFullscreen().catch(() => {});
      }
    };

    return (
      <WorkspaceLayout>
        <div
          id="monitor-fullscreen-container"
          className="fixed inset-0 z-50 bg-black flex flex-col"
        >
          {/* Compact header bar — auto-hides on hover */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-2 bg-black/70 backdrop-blur-sm opacity-80 hover:opacity-100 transition-opacity">
            <div className="flex items-center gap-3">
              <span className={`h-3 w-3 rounded-full ${cfg.color}`} />
              <h2 className="font-semibold text-white">{session?.user_name || "Candidat"}</h2>
              <Badge variant="outline" className="gap-1 text-white border-white/30">
                <StatusIcon className="h-3 w-3" />
                {cfg.label}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1 text-white border-white/30 hover:bg-white/10"
                onClick={() => openInNewWindow(focusedUser, session?.user_name)}
              >
                <ExternalLink className="h-4 w-4" />
                Nouvelle fenêtre
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="text-white border-white/30 hover:bg-white/10"
                onClick={toggleFullscreen}
                title="Plein écran"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="text-white border-white/30 hover:bg-white/10"
                onClick={() => setFocusedUser(null)}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* Video — fills entire screen */}
          <video
            ref={(el) => setVideoRef(focusedUser, el)}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-contain"
            style={{ background: "#000" }}
          />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/meetings`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3">
              <Monitor className="h-6 w-6 text-primary" />
              Surveillance écrans
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {meeting?.title}
            </p>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{stats.total} candidats</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 rounded-lg">
            <Wifi className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700 dark:text-green-400">{stats.sharing} actifs</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 rounded-lg">
            <Clock className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">{stats.pending} en attente</span>
          </div>
          {stats.stopped > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 rounded-lg">
              <MonitorOff className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-700 dark:text-red-400">{stats.stopped} coupés</span>
            </div>
          )}

          {/* Search */}
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un candidat..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>

        {/* Mosaic Grid */}
        {filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Monitor className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">Aucun candidat connecté</p>
            <p className="text-sm mt-1">Les écrans apparaîtront ici dès qu'un candidat partagera son écran.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filteredSessions.map((session) => {
              const cfg = STATUS_MAP[session.status] || STATUS_MAP.disconnected;
              const StatusIcon = cfg.icon;
              const isSharing = session.status === "sharing";
              const hasStream = !!remoteStreams.current[session.user_id];

              return (
                <div
                  key={session.user_id}
                  className={`group relative border rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg ring-2 ${cfg.ring} bg-card`}
                >
                  {/* Video area */}
                  <div
                    className="relative bg-black aspect-video cursor-pointer"
                    onClick={() => isSharing && setFocusedUser(session.user_id)}
                  >
                    {/* Always render video element for sharing candidates so ref is available */}
                    {(isSharing || hasStream) ? (
                      <video
                        ref={(el) => setVideoRef(session.user_id, el)}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-white/40">
                        <StatusIcon className="h-8 w-8 mb-2" />
                        <span className="text-xs">{cfg.label}</span>
                      </div>
                    )}

                    {/* Expand overlay on hover */}
                    {isSharing && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <Maximize2 className="h-8 w-8 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Info footer */}
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${cfg.color} ${isSharing ? "animate-pulse" : ""}`} />
                    <span className="text-sm font-medium truncate flex-1">
                      {session.user_name || "Candidat"}
                    </span>
                    {isSharing && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => openInNewWindow(session.user_id, session.user_name)}
                        title="Ouvrir dans une nouvelle fenêtre"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
